import { useTranslation } from 'react-i18next'

const BTN =
  'px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 ' +
  'hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'

interface PaginationProps {
  page: number
  onPageChange: (page: number) => void
  total: number
  totalIsCapped: boolean
  hasMore: boolean
  pageSize: number
}

/**
 * Pager for the leads list endpoints.
 *
 * Those endpoints stop counting at 1000 instead of running an exact COUNT over
 * the whole match set, so `total` is a floor whenever `totalIsCapped` is set.
 * Deriving a page count from it capped every list at page 50 and silently hid
 * the rest. `hasMore` stays exact — the backend over-fetches one row — so it
 * drives the Neste button, and the page count is only shown when the total is
 * a real number.
 */
export default function Pagination({
  page, onPageChange, total, totalIsCapped, hasMore, pageSize,
}: PaginationProps) {
  const { t } = useTranslation('leads')
  const pages = totalIsCapped ? null : Math.max(1, Math.ceil(total / pageSize))

  // Nothing to page through: on the first page with nothing after it.
  if (page === 1 && !hasMore) return null

  return (
    <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mt-4">
      <span>
        {pages !== null ? t('common.pageOf', { page, pages }) : t('common.page', { page })} &middot;{' '}
        {t('common.totalCount', { n: `${total.toLocaleString('nb-NO')}${totalIsCapped ? '+' : ''}` })}
      </span>
      <div className="flex gap-2">
        <button
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page === 1}
          className={BTN}
        >
          {t('common.prev')}
        </button>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={!hasMore}
          className={BTN}
        >
          {t('common.next')}
        </button>
      </div>
    </div>
  )
}
